import React, { useState, useEffect } from 'react'
import { useApi } from '../contexts/ApiContext'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { 
  Building2, 
  MapPin, 
  Phone, 
  Star, 
  Eye
} from 'lucide-react'

const CityHospitalsSection = () => {
  const { getHospitals } = useApi()
  const { user } = useAuth()
  
  const [hospitals, setHospitals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // User location state
  const [userCity, setUserCity] = useState('')
  const [cityHospitals, setCityHospitals] = useState([])
  const [cityHospitalCount, setCityHospitalCount] = useState(0)

  // City mapping function to handle variations
  const mapCityName = (sessionCity) => {
    const cityMappings = {
      'Bengaluru': 'Bangalore',
      'Bangalore': 'Bangalore',
      'Bengalore': 'Bangalore',
      'Mumbai': 'Mumbai',
      'Delhi': 'Delhi',
      'New Delhi': 'Delhi',
      'Kolkata': 'Kolkata',
      'Calcutta': 'Kolkata',
      'Chennai': 'Chennai',
      'Madras': 'Chennai',
      'Hyderabad': 'Hyderabad',
      'Pune': 'Pune',
      'Ahmedabad': 'Ahmedabad',
      'Jaipur': 'Jaipur',
      'Lucknow': 'Lucknow',
      'Kanpur': 'Kanpur',
      'Nagpur': 'Nagpur',
      'Indore': 'Indore',
      'Thane': 'Thane',
      'Bhopal': 'Bhopal',
      'Visakhapatnam': 'Visakhapatnam',
      'Vizag': 'Visakhapatnam',
      'Pimpri-Chinchwad': 'Pune',
      'Patna': 'Patna',
      'Vadodara': 'Vadodara',
      'Ludhiana': 'Ludhiana',
      'Agra': 'Agra',
      'Nashik': 'Nashik',
      'Faridabad': 'Faridabad',
      'Meerut': 'Meerut',
      'Rajkot': 'Rajkot',
      'Kalyan-Dombivali': 'Mumbai',
      'Vasai-Virar': 'Mumbai',
      'Varanasi': 'Varanasi',
      'Srinagar': 'Srinagar',
      'Aurangabad': 'Aurangabad',
      'Navi Mumbai': 'Mumbai',
      'Solapur': 'Solapur',
      'Vijayawada': 'Vijayawada',
      'Kolhapur': 'Kolhapur',
      'Amritsar': 'Amritsar',
      'Noida': 'Delhi',
      'Ghaziabad': 'Delhi',
      'Gurgaon': 'Gurgaon',
      'Gurugram': 'Gurgaon'
    }
    
    return cityMappings[sessionCity] || sessionCity
  }

  // Function to get user's city from session data
  const getUserCity = async () => {
    try {
      if (user && user.id) {
        const { data, error } = await supabase
          .from('sessions')
          .select('location_city')
          .eq('user_id', user.id)
          .eq('session_type', 'login')
          .order('login_time', { ascending: false })
          .limit(1)
          .single()

        if (data && data.location_city) {
          const mappedCity = mapCityName(data.location_city)
          setUserCity(mappedCity)
        } else {
          setUserCity('Mumbai')
        }
      } else {
        setUserCity('Mumbai')
      }
    } catch (error) {
      console.error('Error getting user city:', error)
      setUserCity('Mumbai')
    }
  }

  useEffect(() => {
    loadHospitals()
    getUserCity()
  }, [])

  useEffect(() => {
    if (userCity) {
      loadHospitals()
    }
  }, [userCity])

  const loadHospitals = async () => {
    try {
      setLoading(true)
      const data = await getHospitals()
      setHospitals(data || [])
      
      // Filter hospitals by user's city
      if (userCity && data) {
        const cityFilteredHospitals = data.filter(hospital => {
          if (!hospital.city) return false
          // Use case-insensitive comparison and handle city variations
          const hospitalCity = hospital.city.toLowerCase().trim()
          const mappedUserCity = userCity.toLowerCase().trim()
          
          // Also check for partial matches and common variations
          const isExactMatch = hospitalCity === mappedUserCity
          const isPartialMatch = hospitalCity.includes(mappedUserCity) || mappedUserCity.includes(hospitalCity)
          
          return isExactMatch || isPartialMatch
        }).sort((a, b) => {
          // Sort by quality rating (top to low)
          const ratingA = parseFloat(a.quality_rating) || 0
          const ratingB = parseFloat(b.quality_rating) || 0
          return ratingB - ratingA
        })
        setCityHospitals(cityFilteredHospitals)
        setCityHospitalCount(cityFilteredHospitals.length)
      }
    } catch (err) {
      console.error('Error loading hospitals:', err)
      setError('Failed to load hospitals')
    } finally {
      setLoading(false)
    }
  }

  const formatPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return 'N/A'
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800'
      case 'INACTIVE': return 'bg-red-100 text-red-800'
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'IN_NETWORK': return 'bg-green-100 text-green-800'
      default: return 'bg-green-100 text-green-800'
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'GOVERNMENT': return 'bg-blue-100 text-blue-800'
      case 'PRIVATE': return 'bg-purple-100 text-purple-800'
      case 'SEMI_PRIVATE': return 'bg-orange-100 text-orange-800'
      case 'MULTI_SPECIALTY': return 'bg-purple-100 text-purple-800'
      case 'MULTI SPECIALTY': return 'bg-purple-100 text-purple-800'
      default: return 'bg-purple-100 text-purple-800'
    }
  }

  const handleViewAll = () => {
    // Navigate to full hospitals page with city filter applied
    const cityParam = userCity ? `&city=${encodeURIComponent(userCity)}` : ''
    window.location.href = `/customer/hospitals?user_id=${user?.id}${cityParam}`
  }

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Network Hospitals</h2>
        </div>
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading hospitals...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      <div className="flex justify-between items-center p-6 border-b border-gray-100">
        <h2 className="text-xl font-semibold text-gray-900">Network Hospitals</h2>
        <button 
          onClick={handleViewAll}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm sm:text-base whitespace-nowrap"
        >
          <Eye className="h-4 w-4" />
          <span className="hidden sm:inline">View All</span>
          <span className="sm:hidden">All</span>
        </button>
      </div>
      
      {/* City-based Hospital Section */}
      {userCity && (
        <div className="bg-blue-50 border-b border-blue-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div className="flex-1">
              <h3 className="text-base sm:text-lg font-semibold text-blue-900 flex items-center">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Hospitals in {userCity}
              </h3>
              <p className="text-xs sm:text-sm text-blue-700">
                {cityHospitalCount > 0 ? `${cityHospitalCount} network hospitals available in your city` : `No hospitals found in ${userCity} - showing all hospitals`}
              </p>
            </div>
            <button 
              onClick={handleViewAll}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center gap-1 transition-colors text-xs sm:text-sm whitespace-nowrap self-start"
            >
              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>View All</span>
            </button>
          </div>
          
          {/* City Hospital Cards */}
          {cityHospitalCount > 0 ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cityHospitals.slice(0, 6).map((hospital) => (
                  <div
                    key={hospital.hospital_id}
                    className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    {/* Header */}
                    <div className="mb-3">
                      <div className="flex items-start space-x-2 mb-3">
                        <div className="bg-blue-100 p-1.5 rounded-lg">
                          <Building2 className="h-3 w-3 text-blue-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1">{hospital.hospital_name || 'N/A'}</h4>
                      </div>
                      
                      {/* Labels Section */}
                      <div className="space-y-1 mb-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Code:</span>
                          <span className="font-medium text-gray-900">{hospital.hospital_code || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Network:</span>
                          <span className={`px-1 py-0.5 text-xs rounded-full font-medium ${getStatusColor(hospital.network_status)}`} style={{ fontSize: '10px' }}>
                            {hospital.network_status || 'IN_NETWORK'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Type:</span>
                          <span className={`px-1 py-0.5 text-xs rounded-full font-medium ${getTypeColor(hospital.hospital_type)}`} style={{ fontSize: '10px' }}>
                            {hospital.hospital_type || 'MULTI SPECIALTY'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <MapPin className="h-3 w-3" />
                        <span className="text-xs">
                          {hospital.city || 'N/A'}, {hospital.state || 'N/A'}
                        </span>
                      </div>
                      
                      {hospital.contact_phone && (
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Phone className="h-3 w-3" />
                          <span className="text-xs">{formatPhone(hospital.contact_phone)}</span>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="space-y-1 mb-2">
                      {hospital.bed_count && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Beds</span>
                          <span className="font-medium text-xs">{hospital.bed_count}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Quality Rating</span>
                        <div className="flex items-center space-x-1">
                          <Star className="h-3 w-3 text-yellow-400 fill-current" />
                          <span className="font-medium text-xs">{hospital.quality_rating || 'N/A'}/5</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-3 border-t border-gray-100">
                      <button
                        onClick={() => window.location.href = `/customer/hospitals?user_id=${user?.id}`}
                        className="w-full flex items-center justify-center gap-1 text-[10px] sm:text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-1 px-2 rounded-lg transition-colors"
                      >
                        <Eye className="h-3 w-3 flex-shrink-0" />
                        <span className="hidden xs:inline">View Details</span>
                        <span className="xs:hidden">Details</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              {cityHospitalCount > 6 && (
                <div className="mt-4 text-center">
                  <button
                    onClick={handleViewAll}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    View {cityHospitalCount - 6} more hospitals in {userCity} →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center py-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  No hospitals found in {userCity}. Showing top hospitals from all locations:
                </p>
              </div>
              
              {/* Show top 6 hospitals as fallback */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hospitals.sort((a, b) => {
                  const ratingA = parseFloat(a.quality_rating) || 0
                  const ratingB = parseFloat(b.quality_rating) || 0
                  return ratingB - ratingA
                }).slice(0, 6).map((hospital) => (
                  <div
                    key={hospital.hospital_id}
                    className="bg-white border border-blue-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    {/* Header */}
                    <div className="mb-3">
                      <div className="flex items-start space-x-2 mb-3">
                        <div className="bg-blue-100 p-1.5 rounded-lg">
                          <Building2 className="h-3 w-3 text-blue-600" />
                        </div>
                        <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1">{hospital.hospital_name || 'N/A'}</h4>
                      </div>
                      
                      {/* Labels Section */}
                      <div className="space-y-1 mb-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Code:</span>
                          <span className="font-medium text-gray-900">{hospital.hospital_code || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Network:</span>
                          <span className={`px-1 py-0.5 text-xs rounded-full font-medium ${getStatusColor(hospital.network_status)}`} style={{ fontSize: '10px' }}>
                            {hospital.network_status || 'IN_NETWORK'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Type:</span>
                          <span className={`px-1 py-0.5 text-xs rounded-full font-medium ${getTypeColor(hospital.hospital_type)}`} style={{ fontSize: '10px' }}>
                            {hospital.hospital_type || 'MULTI SPECIALTY'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <MapPin className="h-3 w-3" />
                        <span className="text-xs">
                          {hospital.city || 'N/A'}, {hospital.state || 'N/A'}
                        </span>
                      </div>
                      
                      {hospital.contact_phone && (
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Phone className="h-3 w-3" />
                          <span className="text-xs">{formatPhone(hospital.contact_phone)}</span>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="space-y-1 mb-2">
                      {hospital.bed_count && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">Beds</span>
                          <span className="font-medium text-xs">{hospital.bed_count}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Quality Rating</span>
                        <div className="flex items-center space-x-1">
                          <Star className="h-3 w-3 text-yellow-400 fill-current" />
                          <span className="font-medium text-xs">{hospital.quality_rating || 'N/A'}/5</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-3 border-t border-gray-100">
                      <button
                        onClick={() => window.location.href = `/customer/hospitals?user_id=${user?.id}`}
                        className="w-full flex items-center justify-center gap-1 text-[10px] sm:text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-1 px-2 rounded-lg transition-colors"
                      >
                        <Eye className="h-3 w-3 flex-shrink-0" />
                        <span className="hidden xs:inline">View Details</span>
                        <span className="xs:hidden">Details</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 text-center">
                <button
                  onClick={handleViewAll}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View all hospitals →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default CityHospitalsSection
